import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Result, Skeleton } from 'antd-mobile'
import { useEffect, useState } from 'react'
import { login } from '@/utils/auth'
// 导入封装的 token 工具函数
import {
  checkAccessToken,
  checkRefreshToken,
  refreshTokenWithLock,
  storeAccessToken,
  storeRefreshToken,
} from '@/utils/tokenUtils' // 新增导入
import { LoginToast, TokenToast } from '@/utils/toastBus'
import { useMutation } from '@tanstack/react-query'
import { captureMutationError } from '@/hook/useQueryOptions'
import Sentry from '@/sentry'
import { useSetInterval } from '@/hook/useSetInterval'
import { ApiError } from '@/types/api'
function handleWeiLogin() {
  const appId = 'wx42e4274305b61fb9'
  // #region get redirect_uri_without_code
  const currentURL = new URL(window.location.href)
  const currentURLSearchParams = new URLSearchParams(window.location.search)
  currentURLSearchParams.delete('code')
  currentURL.search = currentURLSearchParams.toString()
  const redirectUriWithoutCode = currentURL.toString()
  // #endregion
  const baseURL = 'https://open.weixin.qq.com/connect/oauth2/authorize'
  const searchParams = new URLSearchParams({
    appid: appId,
    redirect_uri: redirectUriWithoutCode,
    response_type: 'code',
    scope: 'snsapi_base',
    state: '123',
  })
  const hash = '#wechat_redirect'

  const fullLoginUrl = new URL(baseURL)
  fullLoginUrl.search = searchParams.toString()
  fullLoginUrl.hash = hash

  window.location.replace(fullLoginUrl)
}
// 验证重定向URL的安全性
const getValidRedirect = (redirectParam: string | null): string => {
  if (!redirectParam) return '/courses'

  // 解码URL参数
  try {
    const decoded = decodeURIComponent(redirectParam)
    // 验证是相对路径且不是以 // 开头
    if (decoded.startsWith('/') && !decoded.startsWith('//')) {
      // 防止重定向到登录页面造成无限循环
      if (decoded.startsWith('/login')) {
        return '/courses'
      }
      return decoded
    }
  } catch {
    // 解码失败时使用默认路径
  }
  return '/courses'
}
const refreshPageTimeout = 3000
export const Route = createFileRoute('/login')({
  component: LoginPage,
})
function LoginPage() {
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const redirect = urlParams.get('redirect')
  const [refreshEffectCounter, setRefreshEffectCounter] = useState(0)
  const navigate = useNavigate()
  const {
    mutate: loginFn,
    isPending: loginPending,
    isSuccess,
  } = useMutation({
    mutationFn: login,
    onSuccess: (jwt) => {
      LoginToast.loginSuccess()
      storeAccessToken(jwt.access_token)
      storeRefreshToken(jwt.refresh_token)
      window.history.back()
    },
    onError: captureMutationError(() => {
      LoginToast.loginFail()
      handleWeiLogin()
    }),
    onMutate: () => {
      LoginToast.logining()
    },
    retry: false,
  })
  const { mutate: refreshToken, isPending: refreshPending } = useMutation({
    mutationFn: refreshTokenWithLock,
    onSuccess() {
      TokenToast.refreshTokenSuccess()
      navigate({ to: '/courses', replace: true })
    },
    onError() {
      Sentry.captureException(new Error('刷新 token 失败'))
      TokenToast.refreshTokenFail()
    },
    onMutate() {
      TokenToast.refreshToken()
    },
    retry: (failCount, error) => {
      if (error instanceof ApiError) {
        return false
      }
      return failCount < 3
    },
  })
  useSetInterval(
    () => {
      if (!refreshPending && !loginPending) {
        setRefreshEffectCounter((prev) => prev + 1)
      }
    },
    refreshPageTimeout,
    [refreshPending, loginPending],
  )
  useEffect(() => {
    // 使用封装的工具函数替换原有验证逻辑
    const isAccessTokenValid = checkAccessToken()
    const isRefreshTokenValid = checkRefreshToken()
    if (isAccessTokenValid) {
      navigate({ to: getValidRedirect(redirect), replace: true })
      return
    }
    if (isRefreshTokenValid) {
      refreshToken()
      return
    }
  }, [ refreshEffectCounter, isSuccess ])

  useEffect(() => {
    if (checkAccessToken()) return
    if (checkRefreshToken()) return
    if (!code) {
      handleWeiLogin()
      return
    }
    loginFn(code)
  }, [code])
  // 原有 return 部分（不变）
  return isSuccess ? (
    <>
      <Result status="success" title="登录成功" description="自动跳转至首页..."/>
    </>
  ) : (
    <>
      <Skeleton.Title
        animated
        style={{
          width: '100vw',
          height: '50px',
          marginBottom: '10px',
          marginTop: '0',
        }}
      />
      <Skeleton.Paragraph lineCount={10} animated />
    </>
  )
}
